import React, {useEffect, useState} from 'react';
import {Box, Button, Chip, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import SearchComponent from '@components/inputs/search/Search.component';
import {CheckCircleGreen, CheckCircleGrey, GroupAction, Share, Zip} from '@icons/index';
import {
  Invoice,
  invoicesQuery,
  invoicesService,
  InvoiceStatus,
  InvoiceType,
  searchInvoicesEffect
} from '@store/invoices/invoices';
import GenerateBillModal from '@screens/admin/invoices/bills/generate/GenerateBill.modal';
import {Outlet, useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {emitOnce} from '@ngneat/elf';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {NumberExt} from '@utils/extensions/number.extension';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import SelectComponent from '@components/inputs/select/Select.component';
import dayjs from 'dayjs';
import DateRangePickerComponent from '@components/inputs/dateRangePicker/DateRangePicker.component';
import GenerateBillFromExecLinesModal from '@screens/admin/invoices/bills/generate/GenerateBillFromExecLines.modal';
import BillsGroupedActionModal from '@screens/admin/invoices/bills/BillsGroupedAction.modal';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import IconButtonMenuComponent from '@components/buttons/IconButtonMenu.component';
import {Add, Download} from '@mui/icons-material';
import ExpandableIconButtonComponent from '@components/buttons/ExpandableIconButton.component';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';

const BillsTab = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const searchBills = useEffectFn(searchInvoicesEffect);

  const [openGenerateInvoice, setOpenGenerateInvoice] = useState(false);
  const [openGenerateInvoiceNestle, setOpenGenerateInvoiceNestle] = useState(false);
  const [openGenerateInvoiceFromExecLines, setOpenGenerateInvoiceFromExecLines] = useState(false);
  const [openGroupedAction, setOpenGroupedAction] = useState(false);

  const [CSVLoading, setCSVLoading] = useState(false);

  const [{invoices, loading}] = useObservable(invoicesQuery.invoices$);
  const [selectedInvoices] = useObservable(invoicesQuery.selectedInvoices$);
  const [sumNoVatInCents] = useObservable(invoicesQuery.sumNoVatInCents$);


  const [filters] = useObservable(invoicesQuery.filters$);
  const [sortModel] = useObservable(invoicesQuery.sort$);
  const [tablePagination] = useObservable(invoicesQuery.pagination$);

  useEffect(() => {
    searchBills({filters, tablePagination, sort: sortModel});
  }, [filters, searchBills, sortModel, tablePagination]);

  const handleExportCSV = () => {
    setCSVLoading(true);
    invoicesService.getInvoicesCSV().subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
    setCSVLoading(false);
  };

  const handleExportCSVInvoices = () => {
    invoicesService.getInvoicesPiecesCSV().subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleMergePdf = (invoices: any[]) => {
    invoicesService.printInvoices(invoices).subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleDownloadZIP = (invoices: any[]) => {
    invoicesService.downloadInvoicesZIP(invoices).subscribe({
      error: err => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={1} pb="25px" mt="-35px">
      <Stack alignSelf="flex-end" direction="row" alignItems="center" spacing={2}>
        <ExpandableIconButtonComponent
          variant="outlined"
          color="primary"
          onClick={() => setOpenGroupedAction(true)}
          disabled={!selectedInvoices.length}
          startIcon={<GroupAction/>}
          title={t('global.groupedAction')}
        />
        <IconButtonMenuComponent icon={<Download/>} color="primary" variant="outlined" items={[
          {title: t('invoices.bills.exportLinesCSV'), onClick: handleExportCSV},
          {title: t('invoices.bills.exportBillsCSV'), onClick: handleExportCSVInvoices},
        ]}/>
        <IconButtonMenuComponent icon={<Add/>} color="primary" variant="contained" items={[
          {title: t('invoices.bills.generate'), onClick: () => setOpenGenerateInvoice(true)},
          {
            title: t('invoices.bills.generateFromExecLines'),
            onClick: () => setOpenGenerateInvoiceFromExecLines(true)
          },
          {title: t('invoices.bills.generateNestle'), onClick: () => setOpenGenerateInvoiceNestle(true)},
        ]}/>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <DateRangePickerComponent
          size="small"
          inputWidth={140}
          placeholderStart={t('invoices.executions.filters.from')}
          placeholderEnd={t('invoices.executions.filters.to')}
          startDate={filters.from}
          endDate={filters.to}
          handleChangeStartDate={invoicesService.setFilters('from')}
          handleChangeEndDate={invoicesService.setFilters('to')}/>
        <CheckboxComponent
          handleChange={(value) => {
            emitOnce(() => {
              invoicesService.setFilters('filterAccountantTransferred')(value);
              invoicesService.setPagination('page')(1);
            });
          }}
          value={filters.filterAccountantTransferred}
          label={t('invoices.purchaseStatement.filters.hideTransferred')}
        />
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  invoicesService.setFilters('search')(value);
                  invoicesService.setPagination('page')(1);
                });
              }}/>
          </Box>
          <Box width="200px">
            <SelectComponent
              inputWidth={140}
              color="secondary"
              value={filters.status}
              items={InvoiceStatus.selectItems()}
              handleChange={(value) => {
                invoicesService.setFilters('status')(value);
                invoicesService.setPagination('page')(1);
              }}
              placeholder={t('invoices.invoicesValidation.filters.status')}
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2}>
          <TableFiltersComponent
            filters={filters}
            handleSave={invoicesService.setFilters('tableFilters')}
            handleReset={() => invoicesService.resetFilters()}/>
        </Stack>
      </Stack>
      <Stack flex={1}>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.CUSTOM({
              field: 'type',
              label: '',
              renderCell: (params: GridValueGetterParams<Invoice, InvoiceType>) => {
                const invoiceType = params.row.totalAmountNoVatInCents == ((params.row.cancelledAmountNoVatInCents || 0) * -1) && params.row.totalAmountNoVatInCents !== 0 ? InvoiceType.CANCELLED : params?.value || InvoiceType.DEFAULT;
                return <Chip label={t(InvoiceType.getLabel(invoiceType))}
                             sx={{backgroundColor: InvoiceType.getColor(invoiceType)}}></Chip>;
              }
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientInvoice.documentDate',
              color: 'secondary',
              label: t('invoices.bills.table.period'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<Invoice, Invoice>) => {
                return dayjs(params.row.documentDate).format('DD.MM.YYYY');
              },
              width: 100,
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientInvoice.createdAt',
              color: 'secondary',
              label: t('invoices.bills.table.createdAt'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<Invoice, Invoice>) => dayjs(params.row.createdAt).format('DD.MM.YYYY'),
              width: 100,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'invoiceRef',
              label: t('invoices.bills.table.bill'),
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.invoiceRef,
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'organisation.name',
              color: 'secondary',
              label: t('invoices.bills.table.client'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.clientCount > 1 ? t('global.multiple', {count: params.row.clientCount}) : params.row.clientName,
              fullWidth: true,
              minWidth: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'organisationUnit.name',
              color: 'secondary',
              label: t('invoices.bills.table.clientSite'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.unitCount > 1 ? t('global.multiple', {count: params.row.unitCount}) : params.row.unitName,
              fullWidth: true,
              minWidth: 250,
            }),
            COLUMN_TYPES.INPUT({
              field: 'comment',
              label: t('invoices.bills.table.comment'),
              fullWidth: true,
              minWidth: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoice.totalAmountNoVatInCents',
              label: t('invoices.bills.table.amountHT'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => {
                const price = params.row.totalAmountNoVatInCents;
                return price !== undefined ? `${NumberExt.round(price / 100, 2)} ${params.row.currency}` : '-';
              },
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoice.totalAmountVatIncludedInCents',
              label: t('invoices.bills.table.amount'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => {
                const price = params.row.totalAmountVatIncludedInCents;
                return price !== undefined ? `${NumberExt.round(price / 100, 2)} ${params.row.currency}` : '-';
              },
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientInvoice.cancelCount',
              color: 'secondary',
              label: t('invoices.bills.table.cancelCount'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<Invoice, Invoice>) => {
                return params.row.cancelledCount > 0 ? params.row.cancelledCount : '-';
              },
              width: 100,
            }),
            COLUMN_TYPES.TEXT({
              field: 'invoice.cancelledAmount',
              label: t('invoices.bills.table.cancelledAmount'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<Invoice, Invoice>) => {
                const price = params.row.cancelledAmountNoVatInCents;
                return price != null && price != 0 ? `${NumberExt.round((price * -1) / 100, 2)} ${params.row.currency}` : '-';
              },
              width: 150,
            }),
            COLUMN_TYPES.SELECT({
              field: 'invoice.status',
              label: t('global.status'),
              options: InvoiceStatus.selectItems(),
              sortable: true,
              disabled: () => true,
              valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.status,
              width: 150,
            }),
            COLUMN_TYPES.ICON({
              field: 'accountingTransferDate',
              label: t('invoices.bills.table.accountingTransferDate'),
              renderIcon: (invoiceLine: Invoice) =>
                invoiceLine.accountingTransferDate ? <CheckCircleGreen/> : <CheckCircleGrey/>,
              width: 140,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (invoice: Invoice) => navigate(`${invoice.id}`),
              otherActions: [
                {
                  type: OtherActionType.PDF,
                  handleClick: (invoice: Invoice) => {
                    invoicesService.printInvoice(invoice.id).subscribe({
                      error: (err) => enqueueSnackbar(err.text, err.options),
                    });
                  },
                },
                {
                  type: OtherActionType.SHARE,
                },
              ],
            }),
          ]}
          handleReload={() => searchBills({filters, tablePagination, sort: sortModel})}
          handlePageSizeChange={invoicesService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => invoicesService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={invoicesService.setSort}
          sortModel={[sortModel]}
          handleRowSelected={invoicesService.setSelectedInvoices}
          rowsSelected={selectedInvoices}
          totalRows={invoicesQuery.totalRows}
          data={invoices}
        />
        <Stack direction="row" alignItems="center" alignSelf="flex-end" pt="25px" spacing={2}>
          <TextFieldComponent
            color="secondary"
            label={t('invoices.bills.table.totalAmount')}
            value={NumberExt.toCurrency(sumNoVatInCents / 100)}
            readOnly={true}
          />
          <Button
            variant="outlined"
            color="primary"
            disabled={!selectedInvoices.length}
            onClick={() => handleDownloadZIP(selectedInvoices)}
            startIcon={<Zip style={{color: '#172134', transform: 'scale(0.9)'}}/>}
            size="medium"
          >
            {t('invoices.invoicesValidation.details.zip')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={!selectedInvoices.length}
            startIcon={<Download/>}
            onClick={() => handleMergePdf(selectedInvoices)}
            size="medium">
            {t('global.print')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={!selectedInvoices.length}
            startIcon={<Share/>}
            onClick={() => navigate('send')}
            size="medium">
            {t('global.send')}
          </Button>
        </Stack>
      </Stack>
      {openGenerateInvoice && <GenerateBillModal handleClose={() => setOpenGenerateInvoice(false)}/>}
      {openGenerateInvoiceNestle &&
          <GenerateBillModal forNestle={true} handleClose={() => setOpenGenerateInvoiceNestle(false)}/>}
      {openGenerateInvoiceFromExecLines && (
        <GenerateBillFromExecLinesModal handleClose={() => setOpenGenerateInvoiceFromExecLines(false)}/>)}
      {openGroupedAction && (<BillsGroupedActionModal handleClose={() => setOpenGroupedAction(false)}/>)}
      <Outlet/>
    </Stack>
  );
};

export default BillsTab;
